import {
  PropsWithChildren,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { AnimatedDialog } from '../01-core/AnimatedDialog';
import { OptionalLink, OptionalLinkProps } from '../01-core/OptionalLink';
import { Logo, LogoProps } from '../02-atoms/Logo';
import { Button, ButtonProps } from '../02-atoms/Button';
import { Menu, MenuProps } from '../03-molecules/Menu';
import { ButtonGroup } from '../03-molecules/ButtonGroup';
import { MegaMenuContext } from './MegaMenuContext';

export interface MegaMenuLinkProps extends OptionalLinkProps {
  /** The link label */
  label: string;
}

export interface MegaMenuGroupProps extends MenuProps, OptionalLinkProps {
  /** The group label */
  label?: string;
}

export interface MegaMenuFooterGroupProps extends MegaMenuLinkProps {
  /** The group items */
  items: MegaMenuLinkProps[];
}

export interface MegaMenuProps extends PropsWithChildren {
  /** The menu label */
  label?: string;
  /** The unique ID of the mega menu */
  id?: string;
  /** Optional logo */
  logo?: LogoProps;
  /** Optional title */
  title?: string;
  /** Optional sub-title */
  subtitle?: string;
  /** Optional description */
  description?: string;
  /** Optional menu groups */
  groups?: MegaMenuGroupProps[];
  /** Optional distribution groups */
  distribution?: (MegaMenuProps & { label: string })[];
  /** Optional footer menu groups */
  footer?: MegaMenuFooterGroupProps[];
  /** Optional footer actions */
  footerActions?: ButtonProps[];
  /** Optional footer meta menu items */
  footerMeta?: MegaMenuLinkProps[];
  /** Optional close label */
  close?: string;
  /** Whether to render a close button or not */
  closeButton?: boolean;
}

/** Block rendering a mega menu */
export const MegaMenu = ({
  id,
  logo,
  title,
  subtitle,
  description,
  groups,
  distribution,
  close,
  closeButton = true,
  footer,
  footerActions,
  footerMeta,
  children,
  ...props
}: MegaMenuProps) => {
  const ctx = useContext(MegaMenuContext);
  if (!ctx) {
    throw new Error('MegaMenuContext missing Provider');
  }
  const [open, doOpen, doClose] = ctx;
  const [stateId] = useState(id || 'mega-menu-' + uuidv4());
  const firstRender = useRef<boolean>(true);

  const isOpen = stateId === open;

  // Close on location change
  const location = useLocation();
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (isOpen) {
      doClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (distribution) {
    distribution = distribution.map((group, i) => {
      group.id = id + '-group-' + i;
      return group;
    });
  }

  return (
    <>
      <AnimatedDialog
        id={id}
        open={isOpen}
        duration={250}
        className={[
          'mega-menu',
          groups ? '' : 'mega-menu--no-groups',
          closeButton ? 'mega-menu--close-button' : '',
        ].join(' ')}
        {...props}
      >
        {closeButton && (
          <Button
            className="mega-menu__close"
            title={close}
            type="primary"
            icon="cancel"
            onClick={() => {
              doClose();
            }}
            aria-haspopup="true"
            aria-controls={id}
            aria-expanded={isOpen ? 'true' : 'false'}
          />
        )}
        {logo && <Logo className="mega-menu__logo" {...logo} />}
        <div className="mega-menu__content">
          <div className="mega-menu__container">
            {(title || subtitle) && (
              <h2 className="mega-menu__title">
                {title && (
                  <span className="mega-menu__title-main">{title} </span>
                )}
                {subtitle && (
                  <span className="mega-menu__title-sub">{subtitle}</span>
                )}
              </h2>
            )}
            {description && (
              <p className="mega-menu__description">{description}</p>
            )}
            {!!distribution?.length && (
              <ul className="mega-menu__distribution-groups">
                {distribution.map((group, i) => {
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => {
                          doOpen(group.id!);
                        }}
                        aria-haspopup="true"
                        aria-controls={group.id}
                        aria-expanded={open === group.id ? 'true' : 'false'}
                      >
                        {group.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {children}
            {!!groups?.length && (
              <ul className="mega-menu__groups">
                {groups.map((group, i) => {
                  return (
                    <li key={i} className="mega-menu__group">
                      <h3>
                        <OptionalLink {...group} title={undefined}>
                          <span>{group.label}</span>
                        </OptionalLink>
                      </h3>
                      <Menu items={group.items} />
                    </li>
                  );
                })}
              </ul>
            )}
            {(!!footer?.length ||
              !!footerActions?.length ||
              !!footerMeta?.length) && (
              <div className="mega-menu__footer">
                {!!footerActions?.length && (
                  <ButtonGroup
                    className="mega-menu__footer-actions"
                    compact={true}
                    type="primary"
                    size="small"
                    items={footerActions}
                  />
                )}
                {!!footer?.length && (
                  <ul className="mega-menu__footer-groups">
                    {footer.map((group, i) => {
                      return (
                        <li key={i}>
                          <OptionalLink {...group}>{group.label}</OptionalLink>
                          {group.items && (
                            <ul>
                              {group.items.map((item, i) => {
                                return (
                                  <li key={i}>
                                    <OptionalLink {...item}>
                                      {item.label}
                                    </OptionalLink>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {!!footerMeta?.length && (
                  <ul className="mega-menu__footer-meta">
                    {footerMeta.map((item, i) => {
                      return (
                        <li key={i}>
                          <OptionalLink {...item}>{item.label}</OptionalLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </AnimatedDialog>
      {!!distribution?.length &&
        distribution.map((group, i) => {
          return (
            <MegaMenu key={i} id={group.id} groups={group.groups} close={close}>
              <Button
                className="mega-menu__back"
                type="tertiary"
                icon="arrow_left_triangle"
                label={group.label}
                onClick={() => {
                  doOpen(stateId);
                }}
                aria-haspopup="true"
                aria-controls={group.id}
                aria-expanded={open === group.id ? 'true' : 'false'}
              />
            </MegaMenu>
          );
        })}
    </>
  );
};
